import { getChainDisplayName } from '@passkeys/assets';
import Box from '@passkeys/ui/box';
import ChainIcon from '@passkeys/ui/chain-icon';
import Skeleton from '@passkeys/ui/skeleton';
import Text from '@passkeys/ui/text';
import TokenIcon from '@passkeys/ui/token-icon';

import type { ActivityItem } from '@/ui/flux/tx-log/types';
import { isSendFeeTx } from '@/ui/flux/tx-log/utils';
import useAsset from '@/ui/hooks/use-asset';
import useAssets from '@/ui/hooks/use-assets.js';
import useConvertedAmount from '@/ui/hooks/use-converted-amount';
import { formatDatetime } from '@/utils/datetime';
import Amount from './amount.js';
import CopyableAddress from './copyable-address.js';
import Item from './item.js';

// FIXME: Use better method for this layout.
// 13px of fiat price minus 2px already gained by asset icon.
const amountClassName = 'pb-[calc(12px+13px-2px)]';

export interface DataProps {
  activityItem?: ActivityItem;
}

function Data({ activityItem }: DataProps) {
  const assetName = activityItem?.assetName;
  const order = activityItem?.order;
  const tx = activityItem?.tx;

  const assets = useAssets();
  const toAssetName = order ? order.toAsset : assetName;
  const toAsset = toAssetName ? assets[toAssetName] : undefined;

  const chainAsset = toAsset?.baseAsset;
  const chainAssetDisplayName = chainAsset
    ? getChainDisplayName(chainAsset)
    : undefined;

  const fromAssetName = order?.fromAsset ?? null;
  const fromAsset = useAsset(fromAssetName ?? '');

  const amount = order ? order.toAmount : activityItem?.displayAmount;

  const fromAmount = order?.fromAmount ?? null;

  const fiatAmount = useConvertedAmount({ amount });
  const fiatFromAmount = useConvertedAmount({ amount: fromAmount });
  const fiatFeeAmount = useConvertedAmount({ amount: tx?.feeAmount });

  const toAddress = Array.isArray(tx?.to) ? tx.to[0] : tx?.to;
  const fromAddress = Array.isArray(tx?.from) ? tx.from[0] : tx?.from;

  const loading = !activityItem;

  return (
    <Box className="w-full divide-y divide-black/6 px-3 py-1 theme-dark:divide-neutral-600">
      <Item label="Date">
        {loading ? (
          <Skeleton width={128} height={14} />
        ) : (
          <Text size={14} dataTestId="date-time">
            {formatDatetime(activityItem.date)}
          </Text>
        )}
      </Item>

      <Item label="Chain">
        {chainAsset && chainAssetDisplayName ? (
          <>
            <Text size={14} dataTestId="chain-name">
              {chainAssetDisplayName}
            </Text>
            <ChainIcon
              name={chainAsset.name}
              size={24}
              displayName={chainAssetDisplayName}
            />
          </>
        ) : (
          <>
            <Skeleton width={64} height={14} />
            <Skeleton width={24} height={24} />
          </>
        )}
      </Item>

      {loading && (
        <Item className={amountClassName}>
          <Amount className="col-start-2" />
          <Skeleton width={24} height={24} className="col-start-3" />
        </Item>
      )}

      {!loading && activityItem.type !== 'exchange' && (
        <>
          {toAsset && amount && (
            <Item className={amountClassName} label={activityItem.headerTitle}>
              <Amount
                className="col-start-2"
                cryptoAmount={amount}
                fiatAmount={fiatAmount}
                unit={toAsset.displayTicker}
                received={activityItem.received}
              />

              <TokenIcon
                className="col-start-3"
                name={toAsset.name}
                displayName={toAsset.displayName}
                size={24}
              />
            </Item>
          )}

          {activityItem.sent && toAddress && (
            <Item label="Sent to" className="gap-y-2">
              <CopyableAddress
                value={toAddress}
                dataTestId="sent-to-address-copy-button"
              />
            </Item>
          )}

          {activityItem.received && fromAddress && (
            <Item label="Received from" className="gap-y-2">
              <CopyableAddress
                value={fromAddress}
                dataTestId="sent-from-address-copy-button"
              />
            </Item>
          )}
        </>
      )}

      {!loading && activityItem.type === 'exchange' && (
        <>
          {fromAmount && fromAsset && (
            <Item className={amountClassName} label="You sent">
              <Amount
                className="col-start-2"
                cryptoAmount={fromAmount}
                fiatAmount={fiatFromAmount}
                unit={fromAsset.displayTicker}
              />

              <TokenIcon
                className="col-start-3"
                name={fromAsset.name}
                displayName={fromAsset.displayName}
                size={24}
              />
            </Item>
          )}

          {toAsset && amount && (
            <Item
              className={amountClassName}
              label={activityItem.pending ? 'You will receive' : 'You received'}
            >
              <Amount
                className="col-start-2"
                cryptoAmount={amount}
                fiatAmount={fiatAmount}
                unit={toAsset.displayTicker}
                received
              />

              <TokenIcon
                className="col-start-3"
                name={toAsset.name}
                displayName={toAsset.displayName}
                size={24}
              />
            </Item>
          )}
        </>
      )}

      {!loading && toAsset && !isSendFeeTx(tx) && tx?.feeAmount && (
        <Item className={amountClassName} label="Fee">
          <Amount
            className="col-start-2"
            cryptoAmount={tx.feeAmount}
            fiatAmount={fiatFeeAmount}
            unit={toAsset.displayTicker}
          />

          <TokenIcon
            className="col-start-3"
            name={toAsset.name}
            displayName={toAsset.displayTicker}
            size={24}
          />
        </Item>
      )}
    </Box>
  );
}

export default Data;
